import {
  type AdminUserList,
  type AdminUserRow,
  type AdminUsersListParams,
  type AdminRobotIntelPatch,
  type AdminTradesQueryParams,
  type AdminUserPatch,
  type UserActivityQueryParams,
  adjustUserTokens,
  assignRole,
  type AssignRoleBody,
  createUserTokenBonus,
  fetchAdminOnlineSessions,
  fetchAdminProfileView,
  fetchAdminRoles,
  fetchAdminUser,
  fetchAdminUsers,
  fetchPurchases,
  fetchTokensDetail,
  fetchUserActivity,
  fetchUserRobotIntel,
  fetchUserTradeDetail,
  fetchUserTrades,
  fetchUserTradeStats,
  kickSession,
  reconcileDepositTotalsForEmail,
  type ListSessionsParams,
  type ListUsersParams,
  type PurchasesQueryParams,
  type TokenAdjustmentBody,
  type TokenAdjustmentResponse,
  type TokenBonusCreateBody,
  type TokenBonusCreateResponse,
  type TokensDetailQueryParams,
  patchAdminUser,
  patchUserRobotIntel,
  revokeProfileExtra,
} from "../api";
import { apiClient } from "@/lib/api/client";
import type { components } from "@generated/api/schema";
import { adminOnlineKeys, adminRoleKeys, adminUserKeys } from "../keys";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@/lib/query";
import { makeInvalidator } from "@/lib/query/invalidate";
import { normalizePagination } from "@/lib/query/pagination";

// Paginated admin online-sessions list with smooth transitions.

// /admin/online is a live desk (operator 2026-06-11): 5s foreground poll keeps
// heartbeats/headcount real-time without hammering the API from hidden tabs.
export const ONLINE_SESSIONS_POLL_MS = 5_000;

/** Paginated admin online-sessions. Uses keepPreviousData for smooth page changes. */
export function useAdminOnlineSessions(params: ListSessionsParams = {}) {
  const normalized = normalizePagination(params, { limit: 100 });
  return useQuery({
    queryKey: adminOnlineKeys.list(normalized),
    queryFn: () => fetchAdminOnlineSessions(normalized),
    placeholderData: keepPreviousData,
    refetchInterval: ONLINE_SESSIONS_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

// Admin roles list: GET /api/v2/admin/roles.

/** Paginated admin roles list. */
export function useAdminRoles(
  params: { limit?: number; offset?: number } = {},
) {
  const normalized = normalizePagination(params, { limit: 50 });
  return useQuery({
    queryKey: adminRoleKeys.list(normalized),
    queryFn: () => fetchAdminRoles(normalized),
    placeholderData: keepPreviousData,
  });
}

// Single-user detail with profile-view aggregate.

// List → detail cache seeding (admin cache audit): AdminUserListItemResponse
// is a structural superset of AdminUserResponse, so a row already fetched for
// the list page is a valid instant placeholder for the detail view. The real
// fetch still runs — placeholderData never writes to the cache.
function findCachedAdminUserRow(
  qc: ReturnType<typeof useQueryClient>,
  userId: string,
): AdminUserRow | undefined {
  const pages = qc.getQueriesData<AdminUserList>({
    queryKey: [...adminUserKeys.all, "list"],
  });
  for (const [, page] of pages) {
    const row = page?.items?.find((item) => item.id === userId);
    if (row) return row;
  }
  return undefined;
}

/** One user row from /api/v2/admin/users/{id}. Seeds from cached list rows. */
export function useAdminUserDetail(userId: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: adminUserKeys.detail(userId),
    queryFn: () => fetchAdminUser(userId),
    enabled: Boolean(userId),
    placeholderData: (prev: unknown) =>
      (prev as AdminUserRow | undefined) ?? findCachedAdminUserRow(qc, userId),
  });
}

/** Full profile aggregate from /api/v2/admin/users/{id}/profile-view. */
export function useAdminProfileView(userId: string) {
  return useQuery({
    queryKey: adminUserKeys.profile(userId),
    queryFn: () => fetchAdminProfileView(userId),
    enabled: Boolean(userId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

// Paginated admin users list with smooth filter transitions via placeholderData.

/** Paginated admin user list. Uses keepPreviousData for smooth filter changes. */
export function useAdminUsers(params: AdminUsersListParams = {}) {
  const normalized = normalizeAdminUsersParams(params);
  const shortSearch = isShortAdminUsersSearch(normalized.q);
  return useQuery({
    queryKey: adminUserKeys.list(normalized),
    queryFn: () => fetchAdminUsersInline(normalized, shortSearch),
    placeholderData: shouldKeepAdminUsersPage(normalized.q, shortSearch)
      ? keepPreviousData
      : undefined,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

// Sidebar hover prefetch (admin cache audit): the exact queryKey + queryFn
// pair UsersListPage mounts with (default filters, first page, customers
// team). Owned here so the private param normalization cannot drift from the
// warmup key — a mismatched key would just trigger a second fetch on mount.
export function adminUsersFirstPageQuerySpec(): {
  queryKey: ReturnType<typeof adminUserKeys.list>;
  queryFn: () => Promise<AdminUserList>;
} {
  const normalized = normalizeAdminUsersParams({
    limit: 50,
    offset: 0,
    pipelineStage: "all",
    team: "customers",
  });
  return {
    queryKey: adminUserKeys.list(normalized),
    queryFn: () => fetchAdminUsers(normalized),
  };
}

function normalizeAdminUsersParams(
  params: AdminUsersListParams,
): AdminUsersListParams {
  const { limit, offset } = normalizePagination(listPaginationParams(params), {
    limit: 50,
  });
  const normalized: AdminUsersListParams = { ...params, limit, offset };
  const search = params.q?.trim();
  if (search) normalized.q = search;
  else delete normalized.q;
  return normalized;
}

function listPaginationParams(params: ListUsersParams): {
  limit?: number;
  offset?: number;
} {
  const paginationParams: { limit?: number; offset?: number } = {};
  if (params.limit !== undefined) paginationParams.limit = params.limit;
  if (params.offset !== undefined && params.offset !== null)
    paginationParams.offset = params.offset;
  return paginationParams;
}

function isShortAdminUsersSearch(search: string | null | undefined): boolean {
  const hasSearch = search != null && search.length > 0;
  if (!hasSearch) return false;
  return search.length < 3;
}

function shouldKeepAdminUsersPage(
  _search: string | null | undefined,
  shortSearch: boolean,
): boolean {
  return !shortSearch;
}

function fetchAdminUsersInline(
  params: AdminUsersListParams,
  shortSearch: boolean,
): Promise<AdminUserList> {
  if (!shortSearch) return fetchAdminUsers(params);
  const page = emptyAdminUsersPage(params);
  return Promise.resolve(page);
}

function emptyAdminUsersPage(params: AdminUsersListParams): AdminUserList {
  return {
    items: [],
    total: 0,
    page: 1,
    totalPages: 1,
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
    stats: { totalSubscriptions: 0, activeNow: 0, cancelled: 0, expired: 0 },
    byOrigin: {},
    byPlan: [],
    pipelineStageCounts: {},
  };
}

/** POST /api/v2/admin/deposit-totals/reconcile for one row email. */
export function useReconcileDepositTotalsEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => reconcileDepositTotalsForEmail(email),
    onSuccess: makeInvalidator(qc, adminUserKeys.all),
  });
}

// Admin kick: DELETE /api/v2/admin/online-sessions/{session_id}.

/** Hard-delete one online session row (admin kick). Invalidates sessions list. */
export function useKickSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => kickSession(sessionId),
    onSuccess: makeInvalidator(qc, adminOnlineKeys.all),
  });
}

// Admin role assignment: PUT /api/v2/admin/roles.

/** Assign or update an admin/support role. Invalidates roles list on success. */
export function usePatchRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignRoleBody) => assignRole(body),
    onSuccess: makeInvalidator(qc, adminRoleKeys.all),
  });
}

// Optimistic patch for admin user edits. Invalidates list + detail on success.

/** PATCH /api/v2/admin/users/{user_id} — invalidates list + detail caches. */
export function usePatchUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUserPatch) => patchAdminUser(userId, body),
    onSuccess: makeInvalidator(
      qc,
      adminUserKeys.detail(userId),
      adminUserKeys.all,
    ),
  });
}

// Admin robot-intel patch: PATCH /api/v2/admin/users/{id}/robot-intel.

/** Partially update a user's XP state. Invalidates robot-intel cache. */
export function usePatchUserRobotIntel(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminRobotIntelPatch) =>
      patchUserRobotIntel(userId, body),
    onSuccess: makeInvalidator(qc, adminUserKeys.robotIntel(userId)),
  });
}

// Admin user robot-intel: GET/PATCH /api/v2/admin/users/{id}/robot-intel.

/** Fetch a user's XP/level snapshot for admin inspection. */
export function useUserRobotIntel(userId: string) {
  return useQuery({
    queryKey: adminUserKeys.robotIntel(userId),
    queryFn: () => fetchUserRobotIntel(userId),
    enabled: Boolean(userId),
  });
}

/** Paginated purchases for one user — Compras tab. */
export function usePurchases(
  userId: string,
  params: PurchasesQueryParams = {},
) {
  return useQuery({
    queryKey: adminUserKeys.purchases(
      userId,
      params as Record<string, unknown>,
    ),
    queryFn: () => fetchPurchases(userId, params),
    enabled: Boolean(userId),
    placeholderData: keepPreviousData,
  });
}

/** Paginated token events for one user — Tokens tab. */
export function useTokensDetail(
  userId: string,
  params: TokensDetailQueryParams = {},
) {
  return useQuery({
    queryKey: adminUserKeys.tokensDetail(
      userId,
      params as Record<string, unknown>,
    ),
    queryFn: () => fetchTokensDetail(userId, params),
    enabled: Boolean(userId),
    placeholderData: keepPreviousData,
  });
}

/** POST manual token adjustments and refresh the profile/tokens surfaces. */
export function useAdjustUserTokens(userId: string) {
  const qc = useQueryClient();
  return useMutation<TokenAdjustmentResponse, Error, TokenAdjustmentBody>({
    mutationFn: (body) => adjustUserTokens(userId, body),
    onSuccess: makeInvalidator(qc, adminUserKeys.profile(userId), [
      "admin",
      "users",
      userId,
      "tokens-detail",
    ] as const),
  });
}

// O baú pode conceder tokens, dias de plano e/ou giros; o backend exige pelo
// menos um valor > 0. A recompensa não altera o acesso individual à Central.
export type TokenBonusCreatePayload = TokenBonusCreateBody & {
  readonly premiumDays?: number;
  readonly spins?: number;
};

export type TokenBonusHistoryItem =
  components["schemas"]["TokenBonusHistoryItem"];
type TokenBonusHistoryResponse =
  components["schemas"]["TokenBonusHistoryResponse"];

function userTokenBonusHistoryKey(userId: string) {
  return ["admin", "users", userId, "token-bonuses"] as const;
}

/** Create a redeemable token bonus and refresh profile/tokens surfaces. */
export function useCreateUserTokenBonus(userId: string) {
  const qc = useQueryClient();
  return useMutation<TokenBonusCreateResponse, Error, TokenBonusCreatePayload>({
    mutationFn: (body) => createUserTokenBonus(userId, body),
    onSuccess: makeInvalidator(
      qc,
      adminUserKeys.profile(userId),
      ["admin", "users", userId, "tokens-detail"] as const,
      userTokenBonusHistoryKey(userId),
    ),
  });
}

// GET wrapper mora aqui (não em ../api) até a família token-bonus ganhar um
// módulo próprio; usa o apiClient tipado direto, como createUserTokenBonus.
async function fetchUserTokenBonusHistory(
  userId: string,
): Promise<TokenBonusHistoryResponse> {
  const { data, error } = await apiClient.GET<TokenBonusHistoryResponse>(
    "/api/v2/admin/users/{user_id}/token-bonuses",
    { params: { path: { user_id: userId } } },
  );
  if (error || !data)
    throw new Error(
      `fetchUserTokenBonusHistory failed id=${userId}: ${JSON.stringify(error)}`,
    );
  return data;
}

/** Histórico de baús enviados (mais novo primeiro) — aba Tokens do admin. */
export function useUserTokenBonusHistory(userId: string) {
  return useQuery({
    queryKey: userTokenBonusHistoryKey(userId),
    queryFn: () => fetchUserTokenBonusHistory(userId),
    enabled: Boolean(userId),
  });
}

/** Paginated trades filtered to one user — Trades tab. */
export function useUserTrades(
  userId: string,
  params: Omit<AdminTradesQueryParams, "userId"> = {},
) {
  const merged: AdminTradesQueryParams = { ...params, userId };
  return useQuery({
    queryKey: adminUserKeys.trades(merged as Record<string, unknown>),
    queryFn: () => fetchUserTrades(merged),
    enabled: Boolean(userId),
    placeholderData: keepPreviousData,
  });
}

/** Stats do período com gale agrupado (igual ao histórico do cliente). */
export function useUserTradeStats(
  userId: string,
  params: Omit<
    AdminTradesQueryParams,
    "userId" | "status" | "limit" | "offset"
  > = {},
) {
  const merged = { ...params, userId };
  return useQuery({
    queryKey: adminUserKeys.tradeStats(merged as Record<string, unknown>),
    queryFn: () => fetchUserTradeStats(merged),
    enabled: Boolean(userId),
    placeholderData: keepPreviousData,
  });
}

/** Rich drill-down for one trade — fetched on demand when the row expands. */
export function useUserTradeDetail(userId: string, tradeId: string | null) {
  return useQuery({
    queryKey: adminUserKeys.tradeDetail(userId, tradeId ?? ""),
    queryFn: () => fetchUserTradeDetail(userId, tradeId ?? ""),
    enabled: Boolean(userId) && Boolean(tradeId),
    staleTime: 60_000, // historical trade snapshots are immutable in practice
  });
}

/** Paginated profile activity feed for one admin user profile. */
export function useUserActivity(
  userId: string,
  params: UserActivityQueryParams = {},
) {
  return useQuery({
    queryKey: adminUserKeys.activity(userId, params as Record<string, unknown>),
    queryFn: () => fetchUserActivity(userId, params),
    enabled: Boolean(userId),
    placeholderData: keepPreviousData,
  });
}

/** Revert one active profile extra and refresh profile/activity surfaces. */
export function useRevokeProfileExtra(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (extraId: string) => revokeProfileExtra(userId, extraId),
    onSuccess: makeInvalidator(
      qc,
      adminUserKeys.profile(userId),
      ["admin", "users", userId, "activity"] as const,
      adminUserKeys.all,
    ),
  });
}
